import { Injectable } from '@angular/core';
import { LoadingController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, interval, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class LoadingService {
  private loading: HTMLIonLoadingElement | undefined;
  private readonly LOADING_ID = 'loading';

  private isLoading = false;
  currentCount = 0;
  countSubject = new BehaviorSubject<number>(0);
  stopCounter$ = new Subject<void>();
  counter$: Observable<number> = interval(1000).pipe(
    map(() => (this.currentCount = this.currentCount + 1000)),
    takeUntil(this.stopCounter$)
  );

  constructor(
    private readonly loadingCtrl: LoadingController,
    private readonly translateService: TranslateService
  ) {}

  async presentLoading({
    labelToTranslate = 'LOADING.TITLE',
    forceClose = false,
  }: { labelToTranslate?: string; forceClose?: boolean } = {}): Promise<void> {
    if (this.isLoading && forceClose) {
      await this.dismissLoading();
    }

    if (!this.isLoading) {
      this.isLoading = true;
      let translatedLabel = labelToTranslate;
      try {
        translatedLabel = await this.translateService.get(labelToTranslate).toPromise();
      } catch (error) {
        console.error('Error translating label:', error);
      }

      this.loading = await this.loadingCtrl.create({
        message: translatedLabel,
        id: this.LOADING_ID,
      });

      await this.loading.present();
      this.loading.onDidDismiss().then(() => {
        this.isLoading = false;
      });
    }
  }

  async dismissLoading(): Promise<void> {
    if (this.isLoading) {
      try {
        await this.loading?.dismiss();
      } catch (error) {
        console.error('Error dismissing loading:', error);
      } finally {
        this.loading = undefined;
        this.isLoading = false;
      }
    }
  }

  startCounter(): void {
    this.counter$.subscribe(seconds => {
      this.countSubject.next(seconds);
    });
  }

  getCounter(): Observable<number> {
    return this.countSubject.asObservable();
  }

  stopCounter(): void {
    this.stopCounter$.next();
  }
}
